import { useEffect, useRef, useState } from "react";
import { byName } from "../lib/sort";
import { grantsArea, grantsAny } from "../lib/sections";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { ElectionBanner } from "./ElectionBanner";
import { Logo } from "./Logo";
import { toggleTheme, currentTheme } from "../lib/theme";
import { useCampaign } from "../lib/campaign";
import { useAccounts } from "../lib/accounts";
import { queryData } from "../lib/query";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Select } from "./ui";
import { fmtDateTime } from "../lib/localdate";

// `area` is the section grant on the membership (0314). `votersOnly`/`signsOnly` are the CAMPAIGN
// flags and answer a different question -- does this account run doors, does it run signs -- so an
// entry can need both: Walkbooks is hidden on a signs-only account AND on a manager fenced away
// from cutting turf, and neither check implies the other.
//
// Home has no area. A person with no landing page has no dashboard, and it is the screen that says
// which campaign you are in.
const NAV = [
  { section: "Daily loop" },
  { to: "/", label: "Home", end: true },
  { to: "/universes", label: "Voters & Universes", votersOnly: true, area: "voters" },
  { to: "/surveys", label: "Surveys", votersOnly: true, area: "surveys" },
  { to: "/walkbooks", label: "Walkbooks", votersOnly: true, area: "walkbooks" },
  // CALLS AND TEXTS (0358), beside Walkbooks: the same voters, reached by phone. votersOnly for the
  // reason Walkbooks is -- a signs-only account has nobody to call.
  { to: "/calls", label: "Calls", votersOnly: true, area: "calls" },
  { to: "/texts", label: "Texts", votersOnly: true, area: "texts" },
  // Only on a campaign running the sign programme -- filtered where NAV is rendered, the same rule
  // the field app uses for the Signs tool.
  { to: "/signs", label: "Signs", signsOnly: true, area: "signs" },
  { to: "/analytics", label: "Analytics", area: "analytics" },
  // PULLING DATA OUT IS ITS OWN JOB, not a tool inside the one for looking at it. Granted under
  // `analytics` because that is the section /reports/export itself checks -- a new section key would
  // be a wire change, and every membership that already holds analytics would lose the export.
  { to: "/exports", label: "Export", area: "analytics" },
  { section: "Quality" },
  { to: "/integrity", label: "Integrity", area: "integrity" },
  { section: "Manage" },
  { to: "/import", label: "Import", votersOnly: true, area: "voters" },
  // Tags has no area of its own: the PAGE is offered to anyone with a section that owns a kind of
  // tag, and the page itself shows only those kinds. A sign manager gets the page and sees sign
  // tags. Caleb asked for "signs & tags", not for tags as a separate thing to grant.
  { to: "/tags", label: "Tags", anyArea: ["signs", "voters", "surveys"] },
  // THE PAGE OPENS FOR EITHER GRANT; THE TABS ON IT DO NOT.
  //
  // Two sections meet on this page and they are not the same permission. The roster is `people`.
  // Timesheets is `analytics` -- deliberately, and Caleb drew the line: *"I don't think I want every
  // person who gets access to the users to have access to everybody's time sheets, 'cause some
  // people just create accounts, they don't need to track everybody's time."*
  //
  // So the DOOR takes either (fencing it on `people` would strand an analytics-only manager who had
  // timesheets until #1159 moved the screen), and Users.tsx decides which tabs they get once
  // through it. A nav entry that asked for both would hide the page from somebody holding one.
  { to: "/users", label: "Users", anyArea: ["people", "analytics"] },
  // TWO KINDS OF SETTING ON ONE PAGE (2026-10-12): the campaign's (`account`) and your own alerts
  // (whoever holds `integrity`). Caleb: "it's acceptable to have campaign settings and personal
  // settings all within that campaign section, we just probably need to have different tabs". The
  // door opens for either; Settings.tsx shows only the tabs you hold.
  { to: "/settings", label: "Settings", anyArea: ["account", "integrity"] },
];

type SearchResults = {
  voters: { id: string; name: string; sub: string }[];
  addresses: { id: string | null; street: string; sub: string }[];
  canvassers: { id: string; name: string; sub: string }[];
};
const EMPTY: SearchResults = { voters: [], addresses: [], canvassers: [] };

/** A NAV ENTRY IS A PROMISE. Three things decide whether one may be offered.
 *
 *  THE ACCOUNT: places_enabled (does it run a sign programme) and voters_enabled (does it carry
 *  voter data at all -- 0307, the signs-only account). voters_enabled is undefined on every
 *  campaign predating 0307, so only an explicit false hides anything.
 *
 *  THE PERSON: `sections` on their membership (0314). EMPTY MEANS EVERYTHING THEIR RANK ALLOWS,
 *  which is what every membership held the day it shipped -- so an undefined or empty list must
 *  hide nothing, or the fence ships turned on for everyone.
 *
 *  AND THE NAV IS ONLY MANNERS. The 403 from _require_section is the permission; this stops a
 *  fenced manager being shown a door he cannot open, which is a different job from locking it.
 *
 *  A section header always shows, because each keeps at least one entry under every combination. */
/** WHERE A CAMPAIGN SWITCH LANDS YOU (2026-10-17).
 *
 *  Caleb: "if the page is something that the user would have access to on the other campaign it
 *  makes sense to land them there ... and they wouldn't have access [it] probably needs to land
 *  them on the home page." So: the same area if the new campaign offers it to them (the nav's own
 *  rule, navVisible), else Home. Always the TOP of that area -- a walkbook, household or flag you
 *  had open belongs to the campaign you left -- keeping only `tab`, which says which part of the
 *  page you were on and nothing about the old campaign. Any other parameter (a crew member, a
 *  record) is the old campaign's, and is dropped. The account pages are not a campaign: Home. */
function landingOnSwitch(path: string, search: string,
  c: { places_enabled?: boolean; voters_enabled?: boolean; sections?: string[] } | null): string {
  if (path === "/" || path.startsWith("/account")) return "/";
  const top = "/" + (path.split("/")[1] || "");
  const entry = NAV.find((n: any) => n.to === top);
  if (!entry || !navVisible(c)(entry)) return "/";
  const tab = new URLSearchParams(search).get("tab");
  return tab ? `${top}?tab=${encodeURIComponent(tab)}` : top;
}

function navVisible(current: { places_enabled?: boolean; voters_enabled?: boolean; sections?: string[] } | null) {
  return (n: any) => {
    if ("section" in n) return true;
    if (n.signsOnly && !current?.places_enabled) return false;
    if (n.votersOnly && current?.voters_enabled === false) return false;
    if (n.area && !grantsArea(current, n.area)) return false;
    // ANY-OF, for a page that more than one grant can open. Tags rides on whichever sections own a
    // kind of tag rather than being granted on its own; Users opens for the roster (people) or for
    // the timesheets on it (analytics). This key was called `tagAreas` and described one page --
    // the second caller is what made the name wrong rather than just narrow.
    if (n.anyArea && !grantsAny(current, n.anyArea)) return false;
    return true;
  };
}

export function AppShell() {
  const [theme, setTheme] = useState(currentTheme());
  const { campaigns, current, setCurrent, loading: campaignsLoading, error: campaignsError } = useCampaign();
  const { query: accountsQ } = useAccounts();
  const accounts = queryData(accountsQ) ?? [];
  const navShell = useNavigate();
  const loc = useLocation();
  const onAccount = loc.pathname.startsWith("/account");

  // ── THE SWITCHER SAYS WHERE YOU ARE ────────────────────────────────────────────────────────────
  //
  // The account started life as its own chip beside this selector, on the argument that picking an
  // account is not picking a campaign and one control doing both would silently change which race
  // you were working in. Caleb, using it: *"when you click it it highlights that it's not so
  // intuitive how to get off of it 'cause you can't just unclick it you just have to like click
  // another link"*. He is right, and the argument against folding it in had a hole: it assumed the
  // account row would SET something. It does not. Choosing the account NAVIGATES and leaves
  // `current` exactly as it was, so nothing is changed silently — and choosing a campaign from the
  // account view is then the obvious, in-place way back out, which a highlighted chip never offered.
  //
  // So the control answers "what am I looking at", and its value is READ FROM THE ROUTE rather than
  // held in state. Nothing can drift out of step with the page, because there is no second copy.
  const ACCOUNT = "account:";
  // WHICH account, for somebody who has more than one, is also read from the route. The Account
  // page used to carry its own dropdown for this; two controls over one thing is two answers that
  // can disagree, and the selector in the chrome is the one that already says where you are.
  const accountInUrl = new URLSearchParams(loc.search).get("org");
  const scopeValue = onAccount && accounts.length > 0
    ? ACCOUNT + (accounts.find((a) => a.org_id === accountInUrl)?.org_id ?? accounts[0].org_id)
    : (current?.id ?? "");
  // NAMED FOR WHAT IT IS, NOT FOR WHOSE IT IS. The row used to read "<client name> — whole account",
  // which told somebody their own name back and buried the only word that says what the row does.
  // The client name is on the page they land on, and in the campaign rows right underneath.
  // A person holding TWO accounts is the one case where the name carries information, so it is
  // added only then — otherwise the list would show two identical rows.
  const scopeOptions: [string, string][] = [
    ...accounts.map((a) => [ACCOUNT + a.org_id,
      accounts.length > 1 ? `Orator Account Management — ${a.name}` : "Orator Account Management",
    ] as [string, string]),
    ...[...campaigns].sort(byName((c) => c.name)).map((c) =>
      [c.id, `${c.name}${c.state ? ` · ${c.state}` : ""}`] as [string, string]),
  ];
  function pickScope(v: string) {
    if (v.startsWith(ACCOUNT)) { navShell(`/account?org=${encodeURIComponent(v.slice(ACCOUNT.length))}`); return; }
    const c = campaigns.find((x) => x.id === v);
    if (!c) return;
    setCurrent(c);
    // LEAVING THE ACCOUNT IS THE POINT. Every other page is campaign-scoped already, so picking a
    // campaign there means "take me back to the work" — staying put would leave the selector naming
    // a campaign while the page still showed the account.
    // AND ON A CAMPAIGN PAGE, the same page if the new campaign offers it, else Home -- see
    // landingOnSwitch.
    navShell(landingOnSwitch(loc.pathname, loc.search, c));
  }
  // ── THE RAIL IS 208px, AND THE NUMBER IS DECIDED BY ONE LABEL ────────────────────────────────
  //
  // Caleb, 2026-10-06: *"for the navigation bar on the left I'm wondering if we can maybe make that
  // a little bit more compact to save some room ... it seems like it's taking up more of the screen
  // than it needs to."*
  //
  // It was 232px. What stops it going lower is not taste, it is the longest entry: "Voters &
  // Universes", 18 characters, at the body's inherited 16px semibold. The rail's own chrome is
  // mx-1.5 + px-2.5 on each side -- 32px -- so a 208px rail leaves 176px of label, and 18
  // characters of Segoe UI Semibold at 16px measures around 150. That slack is the margin, and it
  // is why this is 208 rather than the 200 first tried: a nav label that wraps to two lines breaks
  // the vertical rhythm of every row under it, and nothing in CI can see that happen.
  //
  // SO IF A LONGER LABEL IS EVER ADDED, this number is the thing it has to be checked against.
  // Shortening the label is nearly always the better answer than widening the rail.
  //
  // The icons are not here yet. When they are, the row goes back to a flex gap -- the gap-3 that
  // used to sit on the NavLink was removed because a flex gap between ONE child and nothing is
  // inert: it reserved no space, and read as though it did.
  return (
    <div className="grid grid-cols-[208px_1fr] min-h-screen">
      <aside className="bg-surface border-r border-border flex flex-col sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-3 pt-3.5 pb-2.5 font-extrabold text-base tracking-tight">
          <Logo /> Orator
        </div>
        <nav className="flex-1">
          {NAV.filter(navVisible(current)).map((n, i) =>
            "section" in n ? (
              <div key={i} className="px-3 pt-3 pb-1 text-2xs font-bold uppercase tracking-wider text-muted">{n.section}</div>
            ) : (
              <NavLink key={n.to} to={n.to!} end={n.end}
                className={({ isActive }) =>
                  `flex items-center mx-1.5 my-px px-2.5 py-1.5 rounded-lg font-semibold ${
                    isActive ? "bg-accent-weak text-accent" : "text-muted hover:bg-surface-2 hover:text-text"
                  }`
                }>
                {n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="px-3 py-2.5 border-t border-border text-muted text-xs">Powered on · v0.1</div>
      </aside>

      <div className="flex flex-col min-w-0">
        <header className="flex items-center gap-3.5 px-5 py-3 bg-surface border-b border-border sticky top-0 z-10">
          <div className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg font-bold">
            <span className={`w-2 h-2 rounded-full ${campaignsError ? "bg-warn" : "bg-good"}`} />
            {/* A FAILED REQUEST IS NOT AN EMPTY ACCOUNT. This read `campaigns.length === 0` alone, so
                any failure of GET /campaigns -- a 500, a dropped connection, an origin the API's
                CORS_ORIGINS does not list -- rendered as "No campaigns": a confident claim about who
                the signed-in person is, made from a network error. CampaignProvider was already
                recording the reason and nothing read it.
                SAY WHY, for the reason FieldCheckMap says it: a bare "unavailable" throws away the one
                sentence that separates a CORS rejection from a 500 from the network being off. It is
                truncated rather than shortened, so the layout holds and the full text is on the title.
                The loading branch matters too -- campaigns is [] until the first fetch lands, so the
                old code flashed "No campaigns" on every cold load. */}
            {/* A CAMPAIGN FAILURE MUST NOT TAKE THE ACCOUNT DOWN WITH IT. These branches used to be
                ordered error-first, which was right when this control held campaigns alone. Now it
                also holds the way to the account, so it renders whenever there is ANYTHING to pick
                and the campaign failure is said beside it instead of in place of it. */}
            {scopeOptions.length > 0 ? (
              <Select
                variant="bare" hint={onAccount ? "Viewing" : "Campaign"}
                value={scopeValue}
                onChange={pickScope}
                options={scopeOptions}
              />
            ) : campaignsError ? (
              <span className="text-warn max-w-[22rem] truncate" title={campaignsError}>
                Campaigns unavailable: {campaignsError}
              </span>
            ) : campaignsLoading ? (
              <span className="text-muted">Loading campaigns…</span>
            ) : (
              <span className="text-muted">No campaigns</span>
            )}
          </div>
          {scopeOptions.length > 0 && campaignsError && (
            <span className="text-warn text-xs max-w-[18rem] truncate" title={campaignsError}>
              Campaigns unavailable: {campaignsError}
            </span>
          )}
          <GlobalSearch campaignId={current?.id ?? null} />
          <div className="ml-auto flex items-center gap-2.5">
            <button title="Toggle theme" onClick={() => setTheme(toggleTheme())}
              className="w-9 h-9 grid place-items-center border border-border rounded-lg text-muted hover:text-text hover:bg-surface-2">
              {theme === "dark" ? "☀" : "◐"}
            </button>
            <NotificationBell campaignId={current?.id ?? null} />
            <ProfileMenu />
          </div>
        </header>
        {/* KEYED ON THE CAMPAIGN (2026-10-17), so a switch starts every page over. A page reads what
            it remembers when it mounts; without this it kept the old campaign's filters in its own
            state -- Walkbooks went on searching for a person from the campaign you had left, and
            found nothing. Each campaign's remembered view is its own (lib/viewstate), and this is
            what makes the page read the new one. */}
        <ElectionBanner />
        <main key={current?.id ?? "none"} className="p-4 max-w-[1500px] w-full"><Outlet /></main>
      </div>
    </div>
  );
}

function NotificationBell({ campaignId }: { campaignId: string | null }) {
  // A notification is timestamped where the campaign is, not where the reader is sitting.
  const tz = useCampaign().current?.timezone ?? null;
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  async function load() {
    try {
      const q = campaignId ? `?campaign_id=${campaignId}` : "";
      const r = await api<{ unread: number; notifications: any[] }>(`/notifications${q}`);
      setItems(r.notifications || []); setUnread(r.unread || 0);
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); const h = setInterval(load, 20000); return () => clearInterval(h); /* eslint-disable-next-line */ }, [campaignId]);
  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  async function toggle() {
    const wasOpen = open; setOpen(!wasOpen);
    if (!wasOpen && unread > 0) {
      try {
        await api("/notifications/read", { method: "POST", body: JSON.stringify({ campaign_id: campaignId }) });
        setUnread(0); setItems((xs) => xs.map((i) => ({ ...i, read: true })));
      } catch { /* ignore */ }
    }
  }
  return (
    <div ref={ref} className="relative">
      <button title="Notifications" onClick={toggle}
        className="relative w-9 h-9 grid place-items-center border border-border rounded-lg text-muted hover:text-text hover:bg-surface-2">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 grid place-items-center text-3xs font-bold text-white bg-bad rounded-full">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-surface border border-border rounded-lg shadow-lg z-30 py-1 max-h-[60vh] overflow-auto">
          <div className="px-3 py-2 border-b border-border text-2xs uppercase tracking-wider text-muted">Notifications</div>
          {items.length === 0 ? (
            <div className="px-3 py-4 text-sm text-muted">Nothing yet.</div>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => { setOpen(false); if (n.link) nav(n.link); }}
                className={`w-full text-left px-3 py-2 hover:bg-surface-2 ${n.read ? "" : "border-l-2 border-accent"}`}>
                <div className="text-sm font-semibold text-text">{n.title}</div>
                {n.body && <div className="text-xs text-muted mt-0.5">{n.body}</div>}
                <div className="text-3xs text-muted mt-1">{fmtDateTime(tz, n.created_at, "")}</div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ProfileMenu() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const initial = (user?.email ?? "?").charAt(0).toUpperCase();
  useEffect(() => {
    function onDoc(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);
  return (
    <div ref={ref} className="relative">
      <button title="Account" onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 rounded-full bg-accent text-white grid place-items-center font-bold">{initial}</button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-60 bg-surface border border-border rounded-lg shadow-lg z-30 py-1">
          <div className="px-3 py-2 border-b border-border">
            <div className="text-2xs uppercase tracking-wider text-muted">Signed in as</div>
            <div className="text-sm font-semibold text-text truncate">{user?.email ?? "—"}</div>
          </div>
          <button onClick={() => { setOpen(false); nav("/settings"); }}
            className="w-full text-left px-3 py-2 text-sm text-text hover:bg-surface-2">Settings</button>
          <button onClick={() => { setOpen(false); signOut(); }}
            className="w-full text-left px-3 py-2 text-sm text-bad hover:bg-surface-2 font-semibold">Sign out</button>
        </div>
      )}
    </div>
  );
}

function GlobalSearch({ campaignId }: { campaignId: string | null }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<SearchResults>(EMPTY);
  const boxRef = useRef<HTMLDivElement>(null);
  const reqId = useRef(0);

  // run a search (shared by debounce + Enter)
  async function run(term: string) {
    const t = term.trim();
    if (!campaignId || t.length < 2) { setRes(EMPTY); setLoading(false); return; }
    const mine = ++reqId.current;
    setLoading(true);
    try {
      const r = await api<SearchResults>(`/search/global?campaign_id=${campaignId}&q=${encodeURIComponent(t)}`);
      if (mine === reqId.current) { setRes(r); setOpen(true); }
    } catch {
      if (mine === reqId.current) setRes(EMPTY);
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }

  // debounce ~250ms
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setRes(EMPTY); setLoading(false); return; }
    setLoading(true);
    const h = setTimeout(() => run(t), 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, campaignId]);

  // outside-click + Escape close
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  function go(to: string) { setOpen(false); setQ(""); nav(to); }

  const total = res.voters.length + res.addresses.length + res.canvassers.length;
  const showPanel = open && q.trim().length >= 2;

  return (
    <div ref={boxRef} className="relative flex-1 max-w-[420px]">
      <div className="flex items-center gap-2 bg-surface-2 border border-border rounded-lg px-3 py-2 text-muted">
        ⌕
        {/* A placeholder is NOT a label: it is not exposed as the field's accessible name, and some
            screen readers drop it once typing starts. This input sits in a plain <div>, not a
            <label>, so without aria-label it announces as an unnamed "edit text". */}
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { if (q.trim().length >= 2) setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter") run(q); }}
          className="bg-transparent outline-none w-full text-text text-sm"
          aria-label="Search voters, streets and crew"
          name="global-search"
          placeholder="Search voters, streets, crew…" />
        {loading && <span className="w-3.5 h-3.5 rounded-full border-2 border-border border-t-accent animate-spin" />}
      </div>

      {showPanel && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-surface border border-border rounded-lg shadow-lg z-20 max-h-[70vh] overflow-auto py-1">
          {total === 0 && !loading && (
            <div className="px-3 py-3 text-sm text-muted">No matches</div>
          )}
          <Group title="Voters" show={res.voters.length > 0}>
            {res.voters.map((v) => (
              <Row key={"v" + v.id} name={v.name} sub={v.sub} onClick={() => go(`/voters/${v.id}`)} />
            ))}
          </Group>
          <Group title="Addresses" show={res.addresses.length > 0}>
            {/* An address row is a link now (0254). The id can still be null for a door the
                search matched but the licence does not reach, so the fallback stays. */}
            {res.addresses.map((a, i) => (
              <Row key={"a" + i} name={a.street} sub={a.sub} onClick={() => go(a.id ? `/households/${a.id}` : "/universes")} />
            ))}
          </Group>
          <Group title="Crew" show={res.canvassers.length > 0}>
            {res.canvassers.map((c) => (
              <Row key={"c" + c.id} name={c.name} sub={c.sub} onClick={() => go(`/users`)} />
            ))}
          </Group>
        </div>
      )}
    </div>
  );
}

function Group({ title, show, children }: { title: string; show: boolean; children: React.ReactNode }) {
  if (!show) return null;
  return (
    <div className="py-0.5">
      <div className="px-3 pt-1.5 pb-1 text-2xs font-bold uppercase tracking-wider text-muted">{title}</div>
      {children}
    </div>
  );
}

function Row({ name, sub, onClick }: { name: string; sub: string; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="w-full text-left px-3 py-2 hover:bg-surface-2 flex flex-col gap-0.5">
      <span className="text-sm text-text font-semibold truncate">{name}</span>
      {sub && <span className="text-xs text-muted truncate">{sub}</span>}
    </button>
  );
}
